<script setup>
import { computed } from 'vue';
import CardButton from 'shared/components/CardButton.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes } = useMessageContext();

const cards = computed(() => contentAttributes.value?.items || []);
</script>

<template>
  <div class="flex flex-col gap-3 max-w-[380px]" data-bubble-name="cards">
    <!-- Review Cards -->
    <div v-for="(card, index) in cards" :key="index" class="flex flex-col gap-2">
      <div class="border rounded-2xl p-5 bg-n-background-secondary border-n-strong flex flex-col gap-4">
        <!-- Header -->
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-[#6039D5] to-[#A181FF] flex items-center justify-center flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
          <div>
            <h4 v-if="card.title" class="text-sm font-bold text-n-slate-12 leading-tight">
              {{ card.title }}
            </h4>
            <p v-if="card.description" class="text-xs text-n-slate-11 leading-tight mt-0.5">
              {{ card.description }}
            </p>
          </div>
        </div>

        <!-- Platform Buttons Grid -->
        <div v-if="card.actions && card.actions.length" class="grid grid-cols-2 gap-2.5">
          <template v-for="(action, aIndex) in card.actions" :key="aIndex">
            <!-- G2 -->
            <a
              v-if="action.text.toLowerCase().includes('g2')"
              :href="action.uri"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="no-underline flex flex-col items-center gap-2 p-3.5 rounded-xl border border-n-strong bg-n-background hover:border-[#6039D5] transition-all cursor-pointer text-center"
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" class="flex-shrink-0">
                <circle cx="16" cy="16" r="16" fill="#FF492C"/>
                <text x="16" y="21.5" text-anchor="middle" font-family="Arial,sans-serif" font-weight="900" font-size="13" fill="#fff">G2</text>
              </svg>
              <div class="flex flex-col gap-0.5">
                <span class="font-bold text-[11px] text-n-slate-12 leading-tight">G2</span>
                <span class="text-[9px] text-n-slate-11 leading-tight">Leave a review</span>
              </div>
            </a>

            <!-- HostAdvice -->
            <a
              v-else-if="action.text.toLowerCase().includes('hostadvice')"
              :href="action.uri"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="no-underline flex flex-col items-center gap-2 p-3.5 rounded-xl border border-n-strong bg-n-background hover:border-[#2563EB] transition-all cursor-pointer text-center"
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" class="flex-shrink-0">
                <circle cx="16" cy="16" r="16" fill="#2563EB"/>
                <text x="16" y="21.5" text-anchor="middle" font-family="Arial,sans-serif" font-weight="900" font-size="13" fill="#fff">HA</text>
              </svg>
              <div class="flex flex-col gap-0.5">
                <span class="font-bold text-[11px] text-n-slate-12 leading-tight">HostAdvice</span>
                <span class="text-[9px] text-n-slate-11 leading-tight">Leave a review</span>
              </div>
            </a>

            <!-- Fallback Standard Card Button -->
            <CardButton
              v-else-if="!action.text.toLowerCase().includes('trustpilot')"
              :action="action"
            />
          </template>
        </div>

        <!-- Footer note -->
        <p class="text-[10px] text-n-slate-11 text-center leading-normal mt-3">
          Takes less than 60 seconds
        </p>
      </div>
    </div>
  </div>
</template>
